import { 
  Location, 
  CreateLocationRequest, 
  UpdateLocationRequest 
} from '../../types/location.types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

class LocationService {
  private async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    const token = localStorage.getItem('smartdry_token');
    
    console.log(`🔐 Auth token present: ${!!token}`);
    console.log(`🔐 Token preview: ${token ? token.substring(0, 20) + '...' : 'none'}`);
    console.log(`🔐 Full token length: ${token ? token.length : 0}`);
    
    if (!token) {
      console.error('❌ No auth token found in localStorage!');
      throw new Error('No authorization token available');
    }
    
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    };

    console.log(`📡 Request headers:`, headers);

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers,
    });

    console.log(`📡 Response status: ${response.status} ${response.statusText}`);

    if (!response.ok) {
      let errorText = '';

      try {
        errorText = await response.text();
      } catch {
        // Ignore body read errors – we'll fall back to status-only messaging
      }

      const isHtmlError =
        !!errorText &&
        (/<\s*!doctype html/i.test(errorText) || /<html/i.test(errorText));

      // Log a compact error object instead of full HTML
      console.error('❌ API Error Response (locations):', {
        status: response.status,
        statusText: response.statusText,
        isHtmlError,
      });

      // Build a user-friendly, short message
      if (response.status === 524) {
        throw new Error(
          'Locations request timed out. Please try again in a moment.'
        );
      }

      let message = `HTTP error! status: ${response.status}`;

      if (errorText && !isHtmlError) {
        const trimmed =
          errorText.length > 300 ? `${errorText.slice(0, 300)}…` : errorText;
        message += ` - ${trimmed}`;
      }

      throw new Error(message);
    }

    return response;
  }

  async getBusinessLocations(businessId?: string): Promise<Location[]> {
    try {
      // Use the correct backend route: /api/business/locations
      const url = '/business/locations';
      
      console.log(`🌐 API Call: GET ${API_BASE_URL}${url}`);
      console.log(`🔑 Business ID: ${businessId}`);
      
      const response = await this.fetchWithAuth(url);
      const data = await response.json();
      
      console.log(`📡 API Response:`, data);
      
      // Backend returns { locations: [...] } format
      if (data.locations) {
        // Filter only active locations by default
        return data.locations.filter((location: Location) => location.is_active);
      }
      
      // Fallback for error responses
      if (data.error) {
        throw new Error(data.error);
      }
      
      // Return empty array if no locations
      return [];
    } catch (error) {
      console.error('Error fetching locations:', error);
      throw error;
    }
  }

  async getAllBusinessLocations(_businessId?: string): Promise<Location[]> {
    try {
      // Use the same endpoint as getBusinessLocations since backend returns all locations
      const url = '/business/locations';
      
      const response = await this.fetchWithAuth(url);
      const data = await response.json();
      
      console.log(`📡 API Response (all locations):`, data);
      
      // Backend returns { locations: [...] } format
      if (data.locations) {
        // Return all locations (active and inactive)
        return data.locations;
      }
      
      // Fallback for error responses
      if (data.error) {
        throw new Error(data.error);
      }
      
      // Return empty array if no locations
      return [];
    } catch (error) {
      console.error('Error fetching all locations:', error);
      throw error;
    }
  }

  async getLocationById(locationId: string): Promise<Location> {
    try {
      const response = await this.fetchWithAuth(`/business/locations/${locationId}`);
      const data = await response.json();
      
      console.log(`📡 API Response (location by ID):`, data);
      
      // Backend returns { location: {...} } format
      if (data.location) {
        return data.location;
      }
      
      // Fallback for error responses
      if (data.error) {
        throw new Error(data.error);
      }
      
      throw new Error('Location not found');
    } catch (error) {
      console.error('Error fetching location by ID:', error);
      throw error;
    }
  }

  async createLocation(locationData: CreateLocationRequest): Promise<Location> {
    try {
      const response = await this.fetchWithAuth('/business/locations', {
        method: 'POST',
        body: JSON.stringify(locationData),
      });
      
      const data = await response.json();
      
      console.log(`📡 API Response (create location):`, data);
      
      // Backend returns { location: {...} } format
      if (data.location) {
        return data.location;
      }
      
      // Fallback for error responses
      if (data.error) {
        throw new Error(data.error);
      }
      
      throw new Error('Failed to create location');
    } catch (error) {
      console.error('Error creating location:', error);
      throw error;
    }
  }

  async updateLocation(locationData: UpdateLocationRequest): Promise<Location> {
    try {
      const { id, ...updateData } = locationData;
      
      const response = await this.fetchWithAuth(`/business/locations/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });
      
      const data = await response.json();
      
      console.log(`📡 API Response (update location):`, data);
      
      // Backend returns { location: {...} } format
      if (data.location) {
        return data.location;
      }
      
      // Fallback for error responses
      if (data.error) {
        throw new Error(data.error);
      }
      
      throw new Error('Failed to update location');
    } catch (error) {
      console.error('Error updating location:', error);
      throw error;
    }
  }

  async deleteLocation(locationId: string): Promise<void> {
    try {
      const response = await this.fetchWithAuth(`/business/locations/${locationId}`, {
        method: 'DELETE',
      });
      
      const data = await response.json();
      
      console.log(`📡 API Response (delete location):`, data);
      
      // Backend returns { message: '...' } format for successful deletion
      if (data.message) {
        return; // Success
      }
      
      // Fallback for error responses
      if (data.error) {
        throw new Error(data.error);
      }
      
      // Assume success if no error message
      return;
    } catch (error) {
      console.error('Error deleting location:', error);
      throw error;
    }
  }

  async setLocationStatus(locationId: string, isActive: boolean): Promise<Location> {
    try {
      // Use the update location endpoint to change status
      const response = await this.fetchWithAuth(`/business/locations/${locationId}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: isActive }),
      });
      
      const data = await response.json();
      
      console.log(`📡 API Response (set location status):`, data);
      
      // Backend returns { location: {...} } format
      if (data.location) {
        return data.location;
      }
      
      // Fallback for error responses
      if (data.error) {
        throw new Error(data.error);
      }
      
      throw new Error('Failed to update location status');
    } catch (error) {
      console.error('Error updating location status:', error);
      throw error;
    }
  }

  async setPrimaryLocation(locationId: string): Promise<Location> {
    try {
      // Use the update location endpoint to set primary status
      const response = await this.fetchWithAuth(`/business/locations/${locationId}`, {
        method: 'PUT',
        body: JSON.stringify({ is_primary: true }),
      });
      
      const data = await response.json();
      
      console.log(`📡 API Response (set primary location):`, data);
      
      // Backend returns { location: {...} } format
      if (data.location) {
        return data.location;
      }
      
      // Fallback for error responses
      if (data.error) {
        throw new Error(data.error);
      }
      
      throw new Error('Failed to set primary location');
    } catch (error) {
      console.error('Error setting primary location:', error);
      throw error;
    }
  }

  // User preference methods
  async getUserSelectedLocations(): Promise<string[]> {
    try {
      const response = await this.fetchWithAuth('/user/selected-locations');
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch selected locations');
      }
      
      return data.data || [];
    } catch (error) {
      console.error('Error fetching user selected locations:', error);
      // Return empty array as fallback
      return [];
    }
  }

  async saveUserSelectedLocations(locationIds: string[]): Promise<void> {
    try {
      const response = await this.fetchWithAuth('/user/selected-locations', {
        method: 'POST',
        body: JSON.stringify({ location_ids: locationIds }),
      });
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to save selected locations');
      }
    } catch (error) {
      console.error('Error saving user selected locations:', error);
      throw error;
    }
  }
}

export const locationService = new LocationService();
export default locationService;
